import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SEO from '../SEO/SEO';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  HomeIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  StarIcon,
  DocumentCheckIcon,
  SparklesIcon,
  BoltIcon,
  HeartIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import { CheckCircle, User, Briefcase, DollarSign, Ruler, FileText, Globe, Star, Shield, Home } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

const sanitizeAndFormatHtml = (content) => {
  if (!content || typeof content !== 'string') return '';
  let html = content;
  html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
    const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
    if (!attrMatch) return tag;
    const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
    const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
    const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
    return `<a href="${rawUrl}"${targetAttr}>`;
  });
  if (!/<(?:br|p|div)\b/i.test(html)) {
    html = html.replace(/\r?\n/g, '<br />');
  }
  return html;
};

const DEFAULT_CALCULATOR_NOTICE_TEXT = 'Not sure how much home loan you may qualify for? Use our <a href="https://ahamhfc.com/customer-360-eligibility-calculator">home loan eligibility calculator</a> to get an initial estimate before you apply.';

const HomePurchaseLoan = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const handleRichTextClick = (e) => {
    const target = e.target.closest('a');
    if (!target) return;
    const href = target.getAttribute('href');
    const linkTarget = target.getAttribute('target');
    if (!href || linkTarget === '_blank') return;

    if (href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    } else if (href.startsWith('https://ahamhfc.com/') || href.startsWith('http://ahamhfc.com/')) {
      const path = href.replace(/^https?:\/\/ahamhfc\.com/, '');
      if (path.startsWith('/')) {
        e.preventDefault();
        navigate(path);
      }
    }
  };
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [activeTab, setActiveTab] = useState('common');
  const [activeFAQ, setActiveFAQ] = useState(0);  // OPEN FIRST FAQ BY DEFAULT
  const [activeCalc, setActiveCalc] = useState("overview");

  // Dynamic banner state
  const [bannerData, setBannerData] = useState({
    title: 'Buy Your Perfect Home with Aham Home Purchase Loan',
    description: 'Affordable EMIs, flexible tenures, and quick approvals to help you own the home you have always dreamed of.',
    button1_text: 'Apply Now',
    button1_link: 'https://aham.mbwhost.in/contact',
    image_url: 'img/loan-banners/Resale-loan-banner.png'
  });
  const [bannerLoading, setBannerLoading] = useState(true);
  const LOAN_TYPE = 'home_purchase_loan';

  // Overview state
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // Key Benefits state
  const [dynamicBenefits, setDynamicBenefits] = useState([]);
  const [benefitsSettings, setBenefitsSettings] = useState({
    section_title: 'Key Benefits & Features',
    section_subtitle: 'Discover why our home purchase loan is the right choice for you',
    cta_text: 'Request a Quote',
    cta_link: '/contact'
  });
  const [benefitsLoading, setBenefitsLoading] = useState(true);

  // Eligibility state
  const [dynamicEligibility, setDynamicEligibility] = useState([]);
  const [eligibilitySettings, setEligibilitySettings] = useState({
    section_title: 'Home Purchase Loan Eligibility',
    section_subtitle: 'Eligibility for an Aham home purchase loan is simple, and it is always subject to assessment. Here is what we look at.',
    calculator_notice_text: DEFAULT_CALCULATOR_NOTICE_TEXT
  });
  const [eligibilityLoading, setEligibilityLoading] = useState(true);

  // Documents Required state
  const [dynamicDocuments, setDynamicDocuments] = useState({});
  const [documentsSettings, setDocumentsSettings] = useState({
    section_title: 'Documents Required',
    section_subtitle: 'Keep these documents ready for a smooth application process'
  });
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Interest Rates state
  const [dynamicInterestRates, setDynamicInterestRates] = useState([]);
  const [interestSettings, setInterestSettings] = useState({
    section_title: 'Interest Rates & Charges',
    section_subtitle: 'Transparent pricing with competitive rates'
  });
  const [interestLoading, setInterestLoading] = useState(true);

  // FAQ state
  const [dynamicFAQs, setDynamicFAQs] = useState([]);
  const [faqSettings, setFaqSettings] = useState({
    section_title: 'Frequently Asked Questions',
    section_subtitle: 'Find answers to common questions about our home purchase loan'
  });
  const [faqLoading, setFaqLoading] = useState(true);

  // CTA Banner state
  const [ctaBanner, setCtaBanner] = useState(null);
  const [ctaLoading, setCtaLoading] = useState(true);

  // Icon mapping
  const iconMap = {
    HomeIcon,
    BuildingOffice2Icon,
    StarIcon,
    ShieldCheckIcon,
    CurrencyDollarIcon: BanknotesIcon,
    ClockIcon,
    DocumentCheckIcon,
    CheckCircleIcon,
    SparklesIcon,
    BoltIcon,
    HeartIcon,
    LightBulbIcon,
    RocketLaunchIcon,
    ChartBarIcon,
    UserGroupIcon,
    BanknotesIcon,
    KeyIcon,
    CurrencyRupeeIcon,
    UserIcon: UserGroupIcon,
    GlobeAltIcon: Globe,
    BriefcaseIcon: Briefcase,
    DocumentTextIcon,
    Square2StackIcon: DocumentTextIcon,
    IdentificationIcon: DocumentCheckIcon
  };

  // Static fallback data
  const fallbackOverview = {
    title: 'Purchase of an Existing Property (Resale)',
    description: `Buying a pre-owned home can be one of the smartest investments you make — it offers immediate possession, established neighborhoods, and better pricing flexibility. Aham Housing Finance provides Resale Property Home Loans designed to help you purchase a completed home that has been previously owned.`,
    features: [
      'Loan from ₹5 lakhs to ₹50 lakhs – Tailored to meet diverse purchase requirements.',
      'Tenure up to 20 years – Flexible EMI options to match your income flow.',
      'Minimal documentation – Quick processing with No-Income-Proof options for eligible profiles.'
    ],
    image_url: 'img/loan-overview/Purchase-Overview.webp'
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
  };

  // Key Benefits Data
  const benefits = dynamicBenefits.length > 0 ? dynamicBenefits.map(b => ({
    ...b,
    icon: iconMap[b.icon_name] || StarIcon
  })) : [
    {
      icon: BanknotesIcon,
      title: "Loan Amount from ₹5 Lakhs to ₹50 Lakhs",
      description: "Flexible loan amounts to help you purchase your ideal pre-owned home."
    },
    {
      icon: ClockIcon,
      title: "Long Tenure up to 20 Years",
      description: "Comfortable EMI options with repayment terms that fit your cash flow."
    },
    {
      icon: ChartBarIcon,
      title: "Competitive Interest Rates",
      description: "Fixed or floating rates linked to Aham BLR for long-term affordability."
    },
    {
      icon: ShieldCheckIcon,
      title: "Minimal Documentation",
      description: "Hassle-free processing with No-Income-Proof options for eligible profiles."
    },
    {
      icon: CurrencyRupeeIcon,
      title: "Balance Transfer & Top-Up",
      description: "Transfer your existing loan and avail top-up funding for home improvements."
    },
    {
      icon: HomeIcon,
      title: "Support for Salaried, Self-Employed, and NRI Salaried",
      description: "Customized lending solutions for varied income profiles."
    }
  ];




  // Eligibility Criteria
  const eligibility = [
    "Applicant must be between 21 and 60 years (salaried) or 65 years (self-employed) at loan maturity",
    "Minimum monthly income of ₹10,000 for salaried applicants",
    "Minimum business profit of ₹1,80,000 per year for self-employed individuals",
    "Minimum 2 years continuous employment (salaried) or 3 years business vintage (self-employed)",
    "All property co-owners must be co-applicants; spouse inclusion is mandatory",
    "Minimum CIBIL score of 650; higher scores preferred",
    "Property must fall within 50 km of an Aham branch and have clear, marketable title",
    'Good credit history preferred but not mandatory',
  ];



  // Matching icons & colors for each item
  const icons = [
    { icon: User, from: "from-blue-500", to: "to-cyan-400" },
    { icon: Globe, from: "from-green-500", to: "to-lime-400" },
    { icon: Briefcase, from: "from-pink-500", to: "to-rose-400" },
    { icon: DollarSign, from: "from-orange-500", to: "to-amber-400" },
    { icon: FileText, from: "from-purple-500", to: "to-indigo-400" },
    { icon: Shield, from: "from-teal-500", to: "to-emerald-400" },
    { icon: Star, from: "from-yellow-500", to: "to-orange-400" },
    { icon: Ruler, from: "from-red-500", to: "to-orange-400" }
  ];


  // Documents Required
  const documents = {

    common: [
      'Identity Proof (Aadhaar, PAN, Passport, Voter ID)',
      'Address Proof (Utility Bills, Aadhaar)',
      'Recent Passport-size Photographs',
      'Bank Statements (Last 6 months)'
    ],
    salaried: [
      "Completed loan application form with photographs",
      "Identity Proof – Aadhaar, PAN, Passport, Driving License, etc.",
      "Address Proof – Aadhaar, Passport, Utility Bills, Rent Agreement",
      "Last 3 months salary slips and 6 months bank statements",
      "Form 16 and ITR for last 2 years",
      "Registered Sale Deed, approved building plan, valuation report"
    ],

    selfEmployed: [
      "Business registration proof – GST, TIN, Business License",
      "Audited financial statements & ITR for last 3 years",
      "Cash profit and depreciation schedules",
      "Bank statements for last 12 months",
      "Property ownership documents and valuation report",
      "Business License / Trade License"
    ],

    nri: [
      "Valid Indian passport and visa",
      "Overseas employment proof and contract",
      "NRE/NRO bank statements (last 6 months)",
      "Power of Attorney executed in India",
      "Salary slips and income proof in foreign currency",
      "OCI/PIO Card (if applicable)"
    ],
    property: [
      'Land Purchase Agreement / Sale Deed',
      'Title Deed & Encumbrance Certificate',
      'Approved Building Plan from Local Authority',
      'NOC from Society / Local Authority',
      'Construction Estimate / Cost Sheet',
      'Property Tax Receipts',
    ]
  };




  // Schema for SEO
  const purchaseLoanSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions in India.",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions across multiple customer segments in India.",
        "serviceType": [
          "Home Loans",
          "Affordable Housing Finance"
        ],
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/home-purchase-loan/#webpage",
        "url": "https://ahamhfc.com/home-purchase-loan",
        "name": "Home Purchase Loan & Resale Property Finance",
        "description": "Explore Aham Housing Finance home purchase loan options with transparent, competitive and customer-friendly interest rates for buying residential property.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/home-purchase-loan/#loan"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/home-purchase-loan/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/loan-overview/Purchase-Overview-1770203337089-167669003.webp"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "LoanOrCredit",
        "@id": "https://ahamhfc.com/home-purchase-loan/#loan",
        "name": "Home Purchase Loan",
        "url": "https://ahamhfc.com/home-purchase-loan",
        "image": {
          "@id": "https://ahamhfc.com/home-purchase-loan/#primaryimage"
        },
        "description": "Home purchase and resale property finance with transparent, competitive and customer-friendly interest rates.",
        "category": "Housing Finance",
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "audience": {
          "@type": "Audience",
          "audienceType": "Home buyers and resale property buyers"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/home-purchase-loan/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Home Purchase Loan",
            "item": "https://ahamhfc.com/home-purchase-loan"
          }
        ]
      }
    ]
  };


  // FAQs
  const faqs = [
    {
      question: "What is the minimum loan amount available?",
      answer:
        "The minimum loan amount is ₹5 lakhs for Indian residents and ₹10 lakhs for NRI salaried applicants."
    },
    {
      question: "What is the maximum loan tenure?",
      answer:
        "Up to 20 years for purchase and self-construction loans. For NRIs, the maximum tenure is 15 years."
    },
    {
      question: "Can I get a home loan without income proof?",
      answer:
        "Yes. Aham offers low documentation and No-Income-Proof loan options for eligible customers."
    },
    {
      question: "Are co-applicants mandatory?",
      answer:
        "Yes. All co-owners must be co-applicants. If you are the sole owner, spouse inclusion is required."
    },
    {
      question: "What is the maximum LTV ratio?",
      answer:
        "Up to 90% LTV for properties up to ₹30 lakhs, and up to 80% for properties above ₹30 lakhs."
    },
    {
      question: "Can I transfer my existing loan to Aham?",
      answer:
        "Yes. Balance transfer is available with the option for additional top-up funding."
    },
    {
      question: "How quickly is the loan disbursed?",
      answer:
        "Once verification and sanction are completed, loan disbursement is processed promptly as per schedule."
    }
  ];




  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };


  // Scroll Event Listener to auto-switch active tab
  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "benefits", "eligibility", "documents", "interest", "faq"];

      sections.forEach((sec) => {
        const element = document.getElementById(sec);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveCalc(sec);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch dynamic banner data
  useEffect(() => {
    const fetchBannerData = async () => {
      try {
        setBannerLoading(true);
        const response = await axios.get('/api/admin/banners/page/home_purchase_loan');
        const banners = response.data.data || [];

        if (banners.length > 0) {
          // Use first active banner for this page
          const purchaseBanner = banners[0];
          setBannerData({
            title: purchaseBanner.title,
            description: purchaseBanner.subtitle,
            button1_text: purchaseBanner.primary_button_text,
            button1_link: purchaseBanner.primary_button_link,
            image_url: purchaseBanner.image_url
          });
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
      } finally {
        setBannerLoading(false);
      }
    };

    fetchBannerData();
  }, []);

  // Fetch overview data on component mount
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setOverviewLoading(true);
        const response = await axios.get(`/api/admin/loan-overview/${LOAN_TYPE}`);
        const data = response.data.data || null;

        if (data) {
          const parsedData = {
            ...data,
            features: typeof data.features === 'string' ? JSON.parse(data.features) : data.features
          };
          setOverview(parsedData);
        } else {
          setOverview(fallbackOverview);
        }
      } catch (error) {
        console.error('Error fetching overview:', error);
        setOverview(fallbackOverview);
      } finally {
        setOverviewLoading(false);
      }
    };

    const fetchBenefits = async () => {
      try {
        setBenefitsLoading(true);
        const [benefitsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/key-benefits/${LOAN_TYPE}/active`),
          axios.get(`/api/admin/key-benefits/settings/${LOAN_TYPE}`)
        ]);

        if (benefitsRes.data.success) {
          setDynamicBenefits(benefitsRes.data.data);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setBenefitsSettings(prev => ({
            ...prev,
            ...settingsRes.data.data
          }));
        }
      } catch (error) {
        console.error('Error fetching benefits:', error);
      } finally {
        setBenefitsLoading(false);
      }
    };

    const fetchEligibility = async () => {
      try {
        setEligibilityLoading(true);
        const [criteriaRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/eligibility/${LOAN_TYPE}/active`),
          axios.get(`/api/admin/eligibility/settings/${LOAN_TYPE}`)
        ]);

        if (criteriaRes.data.success) {
          setDynamicEligibility(criteriaRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setEligibilitySettings(prev => ({
            ...prev,
            ...settingsRes.data.data,
            calculator_notice_text: settingsRes.data.data.calculator_notice_text || DEFAULT_CALCULATOR_NOTICE_TEXT
          }));
        }
      } catch (error) {
        console.error('Error fetching eligibility data:', error);
      } finally {
        setEligibilityLoading(false);
      }
    };

    const fetchDocuments = async () => {
      try {
        setDocumentsLoading(true);
        const [docsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/documents/${LOAN_TYPE}/active`),
          axios.get(`/api/admin/documents/settings/${LOAN_TYPE}`)
        ]);

        if (docsRes.data.success) {
          // Group documents by category
          const grouped = {};
          (docsRes.data.data || []).forEach(doc => {
            if (!grouped[doc.category]) {
              grouped[doc.category] = [];
            }
            grouped[doc.category].push(doc.document_name);
          });
          setDynamicDocuments(grouped);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setDocumentsSettings(prev => ({
            ...prev,
            ...settingsRes.data.data
          }));
        }
      } catch (error) {
        console.error('Error fetching documents data:', error);
      } finally {
        setDocumentsLoading(false);
      }
    };

    const fetchInterestRates = async () => {
      try {
        setInterestLoading(true);
        const [cardsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/interest-rates/${LOAN_TYPE}/active`),
          axios.get(`/api/admin/interest-rates/settings/${LOAN_TYPE}`)
        ]);

        if (cardsRes.data.success) {
          setDynamicInterestRates(cardsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setInterestSettings(prev => ({
            ...prev,
            ...settingsRes.data.data
          }));
        }
      } catch (error) {
        console.error('Error fetching interest rates data:', error);
      } finally {
        setInterestLoading(false);
      }
    };

    const fetchFAQs = async () => {
      try {
        setFaqLoading(true);
        const [faqsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/faqs/${LOAN_TYPE}/active`),
          axios.get(`/api/admin/faqs/settings/${LOAN_TYPE}`)
        ]);

        if (faqsRes.data.success) {
          setDynamicFAQs(faqsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setFaqSettings(prev => ({
            ...prev,
            ...settingsRes.data.data
          }));
        }
      } catch (error) {
        console.error('Error fetching FAQs data:', error);
      } finally {
        setFaqLoading(false);
      }
    };

    const fetchCTABanner = async () => {
      try {
        setCtaLoading(true);
        const response = await axios.get(`/api/admin/cta-banner/${LOAN_TYPE}`);
        if (response.data.success && response.data.data) {
          setCtaBanner(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching CTA banner data:', error);
      } finally {
        setCtaLoading(false);
      }
    };

    fetchOverview();
    fetchBenefits();
    fetchEligibility();
    fetchDocuments();
    fetchInterestRates();
    fetchFAQs();
    fetchCTABanner();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const headerHeight = 120; // top navbar + sticky navbar height

    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;

    const offsetPosition = elementPosition - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };



  return (
    <div className="min-h-screen bg-white">

      <SEO
        title="Home Purchase Loan & Resale Property Finance | Aham Housing Finance"
        description="Apply for a home purchase loan at attractive interest rates. Finance new flats, plots, or resale properties with easy documentation & fast approval. Apply now!"
        keywords="Aham home purchase loan, Aham Housing Finance home purchase loan, home purchase loan, home loan for buying house, property purchase loan, house purchase loan, resale home loan, resale property finance, home loan for existing property, affordable home purchase loan, home loan India, home loan up to 50 lakhs, flexible EMI home loan, tenure up to 20 years home loan, low documentation home loan, no income proof home loan, home loan for self employed, first time home buyer loan, Aham resale home loan, Aham Housing Finance property loan"
        canonical="https://ahamhfc.com/home-purchase-loan"
        ogTitle="Home Purchase Loan & Resale Property Finance | Aham Housing Finance"
        ogDescription="Apply for a home purchase loan at attractive interest rates. Finance new flats, plots, or resale properties with easy documentation & fast approval. Apply now!"
        schema={purchaseLoanSchema}
      />

      {/* Hero Banner Section */}
      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${bannerData.image_url || 'img/loan-banners/Resale-loan-banner.png'})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
          

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
              {bannerData.title || "Home Purchase Loan"}
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium">
              {bannerData.description || "Own your dream home with Aham's flexible and affordable purchase solutions."}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <a href={bannerData.button1_link || '/contact-aham-housing-finance'} className="w-full sm:w-auto">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto"
                >
                  {bannerData.button1_text || "Apply Now"}
                </motion.button>
              </a>
            </div>
            
              {/* Breadcrumb Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="flex items-center space-x-2 mb-6"
            >
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRightIcon className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">Home Purchase</span>
            </motion.div>
            
          </motion.div>
        </div>
      </section>


      {/* Sticky Finance Navigation */}
      <div className="hidden md:block w-full py-3 sticky top-[80px] z-50">
        <div className="container mx-auto px-1">

          <div className="w-full bg-white rounded-full shadow-lg flex flex-wrap justify-center gap-2 md:gap-6 py-3 px-4">

            {[
              { id: "overview", label: "Overview" },
              { id: "benefits", label: "Benefits" },
              { id: "eligibility", label: "Loan Eligibility Criteria" },
              { id: "documents", label: "Documents" },
              { id: "interest", label: "Interest" },
              { id: "faq", label: "FAQ" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveCalc(tab.id);
                  scrollToSection(tab.id); // ONLY this — smooth scroll with offset
                }}
                className={`
    px-6 py-2 rounded-full font-semibold transition-all 
    ${activeCalc === tab.id
                    ? "bg-yellow-400 text-blue-800 shadow-lg scale-105"
                    : "text-gray-500 hover:text-blue-700"
                  }
  `}
              >
                {tab.label}
              </button>

            ))}
          </div>
        </div>
      </div>


      {/* Overview Section */}

       <section id="overview" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center md:text-left">
                {overview?.title || fallbackOverview.title}
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                {(overview?.description || fallbackOverview.description).split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}

                {(overview?.features || fallbackOverview.features).length > 0 && (
                  <ul className="space-y-3">
                    {(overview?.features || fallbackOverview.features).map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircleIcon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-1" />
                        <span dangerouslySetInnerHTML={{ __html: feature }}></span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={overview?.image_url || fallbackOverview.image_url}
                alt="Aham Housing Finance professional handing over home purchase loan sanction letter for a resale flat to a smiling couple"
                className="rounded-2xl shadow-large w-full"
              />
            </motion.div>

          </div>
        </div>
      </section>









      {/* Key Benefits Section */}
      <section id="benefits" className="relative py-24 overflow-hidden">

        {/* 🔥 Background – gradient + subtle pattern */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-1"
          style={{
            backgroundImage:
              "url('/img/construction-loan/Key-Benefits.webp')",
          }}
        ></div>

        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/100 via-white/70 to-white"></div>

        <div className="relative container mx-auto px-4">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16 px-4"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-white drop-shadow-lg mb-6">
              {benefitsSettings.section_title}
            </h2>
            <p className="text-lg md:text-xl text-white/90 mt-4 max-w-3xl mx-auto leading-relaxed font-medium">
              {benefitsSettings.section_subtitle}
            </p>
          </motion.div>

          {/* Benefit Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -12, scale: 1.03 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="relative bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
              >
                {/* Floating Glow */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-primary-500 rounded-full blur-3xl opacity-20"></div>

                {/* Icon */}
                <div className="bg-gradient-to-br from-primary-600 to-primary-400 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <benefit.icon className="h-9 w-9 text-white" />
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>

                {/* Description */}
                <p className="text-gray-700 leading-relaxed font-medium">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            viewport={{ once: true }}
            className="flex justify-center mt-16"
          >
            <a href={benefitsSettings.cta_link || "/contact-aham-housing-finance"} className="w-full flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl w-[60%] sm:w-auto"
              >
                {benefitsSettings.cta_text || "Request a Quote"}
              </motion.button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Eligibility Criteria Section */}
      <section id="eligibility" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Soft Background Blobs */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary-200/30 blur-3xl rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">

            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-block px-5 py-2 bg-primary-100 text-primary-700 rounded-full font-medium shadow-sm mb-6"
            >
              Loan Eligibility
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-6 mb-4 leading-tight tracking-tight"
            >
              {eligibilitySettings.section_title}
            </motion.h2>

            <p className="text-lg md:text-xl text-gray-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              {eligibilitySettings.section_subtitle}
            </p>

            {(eligibilitySettings.calculator_notice_text || eligibilitySettings.calculator_notice_text === undefined) && (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-6"
              >
                <p
                  onClick={handleRichTextClick}
                  className="text-base md:text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed [&_a]:text-primary-600 [&_a]:underline hover:[&_a]:text-primary-700 [&_a]:font-semibold [&_a]:transition-colors cursor-pointer"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeAndFormatHtml(
                      eligibilitySettings.calculator_notice_text || DEFAULT_CALCULATOR_NOTICE_TEXT
                    )
                  }}
                />
              </motion.div>
            )}
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {(dynamicEligibility.length > 0 ? dynamicEligibility.map(c => ({
              text: c.criteria,
              icon: iconMap[c.icon_name] || CheckCircleIcon,
              from: c.gradient_from || "from-blue-500",
              to: c.gradient_to || "to-cyan-400"
            })) : eligibility.map((text, index) => ({
              text,
              icon: icons[index % icons.length].icon,
              from: icons[index % icons.length].from,
              to: icons[index % icons.length].to
            }))).map((item, index) => {
              const IconComp = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="
                group backdrop-blur-xl bg-white/70 
                border border-white/40 shadow-xl rounded-2xl 
                p-6 flex flex-col items-center text-center gap-5 
                hover:-translate-y-2 hover:shadow-2xl 
                transition-all duration-300
              "
                >
                  {/* Gradient Icon */}
                  <div
                    className={`
                  w-14 h-14 rounded-xl flex items-center justify-center 
                  bg-gradient-to-br 
                  ${item.from ? (item.from.startsWith('from-') ? item.from : `from-${item.from}`) : 'from-blue-500'} 
                  ${item.to ? (item.to.startsWith('to-') ? item.to : `to-${item.to}`) : 'to-cyan-400'}
                  text-white shadow-lg group-hover:scale-105 transition
                `}
                  >
                    {typeof IconComp === 'function' || typeof IconComp === 'object' ? <IconComp className="h-7 w-7" /> : IconComp}
                  </div>

                  <div className="flex-1">
                    <p className="text-gray-800 font-semibold text-lg leading-snug">
                      {item.text}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTA Button */}
          {/* <div className="text-center mt-16">
            <a href="/customer-360-eligibility-calculator">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="
              px-10 py-4 rounded-full text-white font-semibold 
             bg-[#ed2636]
              shadow-lg hover:bg-primary-600 transition
            "
              >
                Check Your Eligibility Now
              </motion.button>
            </a>
          </div> */}

        </div>
      </section>

      {/* Documents Required Section */}
      <section id="documents" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Background blur circles */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-200/20 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-extrabold text-gray-900 mb-4">
              {documentsSettings.section_title}
            </h2>
            <p className="text-xl text-gray-600">
              {documentsSettings.section_subtitle}
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto">

            {/* Premium Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
                { key: "common", label: "Common", icon: <FileText /> },
                { key: "salaried", label: "Salaried", icon: <Briefcase /> },
                { key: "selfEmployed", label: "Self-Employed", icon: <User /> },
                { key: "nri", label: "NRI", icon: <Globe /> },
                { key: "property", label: "Property", icon: <Home /> },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
              flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 backdrop-blur-xl
              border border-white/40 shadow-lg
              ${activeTab === tab.key
                      ? "bg-gradient-to-r from-primary-600 to-primary-500 text-white scale-105 shadow-primary/50"
                      : "bg-white/70 text-gray-700 hover:bg-white/90"
                    }
            `}
                >
                  <span className="text-lg">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content Card */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="backdrop-blur-xl bg-white/70 p-10 rounded-3xl border border-white/40 shadow-xl"
            >
              <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(Object.keys(dynamicDocuments).length > 0 && dynamicDocuments[activeTab] ? dynamicDocuments[activeTab] : documents[activeTab]).map((doc, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    className="
                flex items-start gap-4 
                p-5 bg-gradient-to-br from-white to-gray-50 
                rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-200
              "
                  >
                    <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center shadow-inner">
                      <FileText className="h-6 w-6" />
                    </div>
                    <span className="text-gray-900 font-semibold">{doc}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>

          </div>
        </div>
      </section>


      {/* Interest Rates Section */}
      <section
        id="interest"
        className="relative py-24 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white overflow-hidden"
      >
        {/* Decorative Blurs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl opacity-30"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto text-center"
          >
            {/* Title */}
            <h2 className="text-5xl md:text-6xl font-extrabold mb-8 leading-tight drop-shadow-xl">
              {interestSettings.section_title}
              <span className="block text-primary-200 mt-2">{interestSettings.section_subtitle}</span>
            </h2>

            {/* Description Section */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-10 md:p-14 shadow-2xl">
              <p className="text-xl text-white/90 leading-relaxed mb-6">
                {interestSettings.intro_text || 'Aham Housing Finance brings you transparent, competitive, and customer-friendly interest rates designed to make home purchase affordable and stress-free.'}
              </p>

              <div className="grid md:grid-cols-3 gap-8 my-10">
                {(dynamicInterestRates.length > 0 ? dynamicInterestRates : [
                  { icon: '🏠', title: 'Flexible Rates', description: 'Rates tailored based on loan amount, tenure, credit profile & location.' },
                  { icon: '🔍', title: '100% Transparency', description: 'No hidden fees. No surprise charges. Complete clarity from start to finish.' },
                  { icon: '🤝', title: 'Dedicated Support', description: 'Our expert team guides you at every stage of your purchase journey.' }
                ]).map((card, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 + index * 0.2 }}
                    className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                  >
                    <div className="text-3xl mb-3">{card.icon}</div>
                    <h4 className="text-xl font-semibold mb-2">{card.title}</h4>
                    <p className="text-white/80 text-sm leading-relaxed">
                      {card.description}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Final Message */}
              <p className="text-lg text-white/85 mb-10 leading-relaxed">
                Begin your home construction journey with confidence and clarity.
                Aham ensures the process is smooth, transparent, and tailored to your needs.
              </p>

              {/* CTA Button */}
              {/* <motion.a
                href="https://aham.mbwhost.in/emi-calculator"
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white-700 hover:text-black hover:bg-primary-50 px-10 py-4 rounded-xl font-semibold text-xl transition-all duration-300 shadow-xl"
              >
                Calculate EMI
              </motion.a> */}
            </div>
          </motion.div>
        </div>
      </section>


      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {faqSettings.section_title}
            </h2>
            <p className="text-xl text-gray-600">
              {faqSettings.section_subtitle}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {(dynamicFAQs.length > 0 ? dynamicFAQs : faqs).map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="mb-4"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className={`w-full text-left p-6 rounded-xl transition-all duration-300 ${activeFAQ === index
                    ? 'bg-primary-50 border-2 border-primary-600'
                    : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    {activeFAQ === index ? (
                      <ChevronUpIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
                    ) : (
                      <ChevronDownIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                  {activeFAQ === index && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 text-gray-700 leading-relaxed"
                    >
                      {faq.answer}
                    </motion.p>
                  )}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(img/construction-loan/home-construction-loan.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {ctaBanner?.title || 'Ready to Purchase Your Dream Home?'}
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto text-white/90">
              {ctaBanner?.description || 'Take the first step towards purchasing your perfect home with Aham\'s hassle-free loan process'}
            </p>
            <div className="flex justify-center">
              <motion.a
                href={ctaBanner?.button_link || 'https://aham.mbwhost.in/contact'}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white hover:bg-white hover:text-primary-600 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                {ctaBanner?.button_text || 'Talk to Our Loan Expert'}
              </motion.a>
            </div>

          </motion.div>
        </div>
      </section>


    </div>
  );
};

export default HomePurchaseLoan;